<template>
  <v-container>
    <h3>Title of Research Study:</h3>
    <p>Listener perceptions of different stories and voices </p>
    <h3>Principal Investigator:</h3>
    <p>Annette D'Onofrio</p>
    <h3>Student Investigator:</h3>
    <p>Amelia Stecker</p>
    <h3>Supported By:</h3>
    <p>This research is supported by the Northwestern University Linguistics Department.</p>
    <h3>Key Information about this research study:</h3>
    <p>
      The following is a short summary of this study to help you decide whether to be a part of this study. Information that is more detailed is explained later on in this form.  
      </p>
    <ul class="mb-4">
      <li>The purpose of this study is to learn more about individuals' perceptions about different podcast stories.</li>
      <li>You will be asked to read a series of stories and answer some questions about what you heard for each one. </li>
      <li>We expect that you will be in this research study for 30 minutes.</li>
      <li>There are no anticipated risks or benefits from participating in this stidy, beyond those associated with everyday computer use.</li>
    </ul>

    <h3>Why am I being asked to take part in this research study?</h3>
    <p>We are asking you to take part in this research study because you are 18 years old or older, you live in the United States, and you are a speaker of American English.</p>

    <h3>How many people will be in this study?</h3>
    <p>We expect about 360 people will be in this research study.  </p>

    <h3>What should I know about participating in a research study?</h3>
    <p>
      After listening, you will be asked some questions about what the story and the speaker telling the story. This will take about 5 minutes to complete. You will repeat these steps until you have listened to four speakers. This will about 30 minutes to complete. 
    </p>

    <h3>Will being in this study help me in any way?</h3>
    <p>We cannot promise any benefits to you or others from your taking part in this research.</p>

    <h3>Is there any way being in this study could be bad for me?</h3>
    <p>A possible risk for any research is that confidentiality could be compromised – that is, that people outside the study might get hold of confidential study information.  We will do everything we can to minimize this risk, as described in more detail later in this form. However, we will not be collecting any personally identifiable information during this study.</p>

    <h3>What happens if I do not want to be in this research, or I change my mind later?</h3>
    <p>Participation in research is voluntary. You can decide to participate or not to participate.  If you do not want to be in this study or withdraw from the study at any point, your decision will not affect your relationship with Northwestern University/Northwestern Memorial Healthcare.</p>
    <p>You can leave the research at any time and it will not be held against you. If you decide to withdraw from this study, any data already collected from you will be destroyed.</p>

    <h3>How will the researchers protect my information?</h3>
    <p>We will not be collecting any personally identifiable information about you during this research study. All survey responses will be stored on a secure server accessible only to members of the research team at Northwestern.</p>

    <h3>Who will have access to the information collected during this research study?</h3>
    <p>Efforts will be made to limit the use and disclosure of your personal information, including research study records, to people who have a need to review this information. We cannot promise complete secrecy.</p>
    <p>There are reasons why information about you may be used or seen by other people beyond the research team during or after this study. Examples include:</p>
    <ul class="mb-4">
    <li>University officials, government officials, study funders, auditors, and the Institutional Review Board may need access to the study information to make sure the study is done in a safe and appropriate manner.</li>
    <li>The research team may give information to appropriate authorities for reasons of health and safety – for example, if you indicate that you plan to harm yourself or others, or for public health reasons.</li>
    </ul>

    <h3>How might the information collected in this study be shared in the future?</h3>
    <p>We will keep the information we collect about you during this research study for study recordkeeping.</p>
    <p>De-identified data from this study may be shared with the research community, with journals in which study results are published, and with databases and data repositories used for research. We will remove or code any personal information that could directly identify you before the study data are shared. Despite these measures, we cannot guarantee anonymity of your personal data.</p>
    <p>The results of this study could be shared in articles and presentations, but will not include any information that identifies you.</p>

    <h3>Will I be paid or given anything for taking part in this study?</h3>
    <p>You will receive $5.50 for your participation in this study. </p>
    <!-- <p>If you are registered for a linguistics class at Northwestern University, you need to fulfill an experimental requirement. Your participation in this study will assist in fulfilling this requirement. If you decide to withdraw your consent from this study at any time, you will still receive one credit to satisfy the experimental requirement.</p> -->


   

    <h3>Who can I talk to?</h3>
    <p>
      If you have questions, concerns, or complaints, you can contact the Principal Investigator Dr. Annette D’Onofrio at donofrio@northwestern.edu or the student investigators, Amelia Stecker (astecker@u.northwestern.edu). 
    </p>
    <p>
      This research has been reviewed and approved by an Institutional Review Board (“IRB”) – an IRB is a committee that protects the rights of people who participate in research studies. You may contact the IRB by phone at (312) 503-9338 or by email at irb@northwestern.edu if: 
      <ul>
        <li>
          Your questions, concerns, or complaints are not being answered by the
          research team.
        </li>
        <li>You cannot reach the research team.</li>
        <li>You want to talk to someone besides the research team.</li>
        <li>You have questions about your rights as a research participant.</li>
        <li>You want to get information or provide input about this research.</li>
      </ul>
    </p>
    

    <p>
      If you want a copy of this consent for your records, you can print it from
      the screen.
    </p>
    <p>
      If you wish to participate, please click the "I Agree" button and you will
      be taken to the survey.
    </p>
    <p>
      If you do not wish to participate in this study, please select "I
      Disagree" or select X in the corner of your browser.
    </p>
    <v-row no-gutters class="mt-5 mb-10">
      <v-btn @click="agree()" class="primary mr-5 ">I Agree <v-icon>check</v-icon></v-btn>
      <v-btn @click="disagree()" class="error">I Disagree <v-icon>close</v-icon></v-btn>
      
    </v-row>
  </v-container>
</template>

<script>
//import useFlow from "@/composables/useFlow.js";
import Vue from "vue";
import store from "@/store";
import router from "@/router";
import {
  ref as fbRef,
  getDatabase,
  set,
  serverTimestamp,
} from "firebase/database";

export default {
  name: "Consent",
  setup() {
    const disagree = () => {
      Vue.prototype.$dialog.info({
        title: "Consent Declined",
        text: "You may close the browser and exit the experiment",
      });
    };

    const agree = () => {
      set(
        fbRef(
          getDatabase(),
          store.state.experimentName + "/consent/" + store.state.pid
        ),
        {
          consent: serverTimestamp(),
        }
      );
      store.commit("setLocked", false);
      router.push("/practice");
    };

    return { agree, disagree };
  },
};
</script>

